/**
 * Resolves one favorite team id to a plate that links to that team's card at
 * `/scores?teamId=`. A hook cannot be called in a loop, so one instance of
 * this component resolves one team; mirrors
 * `apps/mobile/src/accountHome/FavoriteTeamPlate.tsx`'s split.
 *
 * The search is an object, not a merge over `prev`, so `accountId` and any
 * `/fan` overlay key stay behind. The skeleton is not a link.
 *
 * A team with a logo draws it on the light logo disc, positioned over the
 * same box so loading it moves nothing; an image that fails falls back to
 * the abbreviation, the same rule as `TeamMark` (ISSUE-1087).
 */
import { useTeamGet } from "@sportsmet/sports-sdk/private";
import { cn, FOCUS_RING, NEUTRAL_SURFACE_FRAME, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

export type typeFanTeamPlateProps = {
  teamId: string;
};

export const FanTeamPlate = (props: typeFanTeamPlateProps): ReactNode => {
  const team = useTeamGet({ teamId: props.teamId });
  const row = team.data?.data?.team;
  const logoUrl = row?.logoUrl ?? null;
  const [hasFailed, setHasFailed] = useState(false);
  useEffect(() => {
    setHasFailed(false);
  }, [logoUrl]);
  const imageUrl =
    logoUrl !== null && logoUrl !== "" && !hasFailed ? logoUrl : null;

  if (team.isPending || row == null) {
    return (
      <Skeleton
        shape="circle"
        data-testid={`fan-team-loading-${props.teamId}`}
      />
    );
  }

  return (
    <Link
      to="/scores"
      search={{ teamId: props.teamId }}
      data-testid={`fan-team-${props.teamId}`}
      className={cn(
        "flex min-h-touch-target flex-col items-center gap-1 rounded-sm text-ink no-underline",
        FOCUS_RING,
      )}
    >
      <span
        aria-hidden="true"
        data-testid="fan-team-mark"
        className={cn(
          "relative flex h-12 min-w-12 max-w-32 items-center justify-center overflow-hidden rounded-full px-2 text-caption text-ink",
          imageUrl !== null ? "bg-mark-logo-surface" : "bg-surface-elevated",
          NEUTRAL_SURFACE_FRAME,
        )}
      >
        {imageUrl !== null ? (
          <img
            src={imageUrl}
            alt=""
            data-testid="fan-team-mark-image"
            className="absolute inset-0 h-full w-full object-contain p-1"
            onError={() => {
              setHasFailed(true);
            }}
          />
        ) : (
          row.abbreviation
        )}
      </span>
      <span className="text-caption text-ink-secondary">{row.name}</span>
    </Link>
  );
};
